<template>
  <div v-if="initialDomainOpt !== domainOpt.NONE" class="domain-name-block" :ref="`domainBlock-${domainIndex}`">

    <div class="domain-options">

      <v-expand-transition>
        <div class="unit" v-show="editableDomainName">
          <v-form ref="form" @submit.prevent="checkDomainName" :class="[{ 'free-domain' : isFreeDomainOption }, 'domain-holder']" >
            <div data-attr="domain-name" class="text-field">
              <v-text-field
                  :placeholder="showFreeDomainOption && isFreeDomainOption ? 'example' : 'example.com'"
                  v-model="domainName"
                  :rules="rulesDomainName"
                  :loading="loading"
                  outlined
                  clearable
                  prefix="www."
                  @keydown.space.prevent
              >
                <template v-slot:message="{ message }">
                  <span v-html="message"></span>
                </template>
              </v-text-field>
            </div>
            <b class="tld">{{freeDomainText}}</b>
            <v-btn
                :ripple="false"
                @click="checkDomainName"
                :loading="loading"
                color="primary"
                data-attr="domain-name-btn"
            >
              {{ $t('actions.buttonCheck') }}
            </v-btn>

            <v-expand-transition>
              <template v-if="domainName && domainStatus !== undefined">
                <div v-if="isValidDomain" class="info-message valid" data-attr="domain-valid">
                  <v-icon class="sr-icon-check"/>
                  <span v-if="isFreeDomain && domainOption.register === 'register'" v-html="$t('domains.successMessages.freeDomain', {domainName: domainNameText})" data-attr="free-domain"></span>
                  <span v-else-if="isTransferToUser" v-html="$t('domains.successMessages.additionalSettings')" data-attr="additional-settings"></span>
                  <span v-else-if="isFreeDomainOption" v-html="$t('domains.successMessages.availableFreeDomain', {domainName: domainNameText})" data-attr="available-free-domain"></span>
                  <span v-else>
                    <span v-html="$t(`domains.successMessages.${domainOption.register}`, {domainName: domainNameText})" :data-attr="`${domainOption.register}`"></span><span style="white-space: nowrap;">&#160;<v-tooltip v-if="domainOption.register === 'transfer'" right max-width="420px" :debounce="200">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                          icon
                          v-bind="attrs"
                          v-on="on"
                          tag="span"
                          fab
                      >
                        <v-icon class="sr-icon-help"/>
                      </v-btn>
                    </template>
                    <div v-html="$t('domains.transferTooltip')"></div>
                    </v-tooltip>
                  </span>
                  </span>
                </div>

                <div v-else class="info-message invalid" data-attr="domain-invalid">
                  <v-icon class="sr-icon-close"/>
                  <span v-if="domainStatus === domainNameStatus.TLD_NOT_SUPPORTED" v-html="$t(`validation.domain.${domainStatus}.${domainOption.register}`, {domainName: domainName})" :data-attr="`${domainStatus} ${domainOption.register}`"></span>
                  <span
                      v-else
                      v-html="$t(`validation.domain.${translationKey}`, {
                        domainName: domainName,
                        maxLength: domainNameResponse?.max_length ?? 63,
                        minLength: domainNameResponse?.min_length ?? 3
                      })"
                      :data-attr="`${domainStatus}`"
                  ></span>
                </div>
              </template>
            </v-expand-transition>

          </v-form>
        </div>
      </v-expand-transition>
    </div>

    <template v-if="!isFreeDomainOption && (isValidDomain && !isTransferToUser)">

      <template v-if="registrationPeriods && registrationPeriods.length > 0">

        <v-row class="unit domain-settings">
          <v-col v-if="registrationPeriods" class="period" cols="12" md="6" :data-attr="`period-${domainOption.register}`">
            <v-select
                v-model="registrationPeriod"
                outlined
                :label="registrationPeriodLabel"
                ref="periods-select"
                :items="registrationPeriods"
                item-value="year"
                item-text="year"
                :class="`periods-select-${domainIndex}`"
                :attach="`.periods-select-${domainIndex}`"
                :menu-props="{ bottom: true, offsetY: true }"
                return-object
                :readonly="isFreeDomain || notRenewedAfterTransfer"
            >
              <template slot="selection" slot-scope="{ item }">
                <span v-if="notRenewedAfterTransfer">{{$t('unavailable') }}</span>
                <span v-else>{{ item.year }} {{ $tc('year', item.year) }} {{ $t('for') }}
                  <span v-html="priceTransform(item.price, currencyInfo.suffix, '', currencyInfo.prefix)"></span>
                </span>
              </template>

              <template slot="item" slot-scope="{ item }">
                <v-list-item-content :data-attr="`renewal-${item.year}-year`">
                <span>{{ item.year }} {{ $tc('year', item.year) }} {{ $t('for') }}
                  <span v-html="priceTransform(item.price, currencyInfo.suffix, '', currencyInfo.prefix)"></span>
                  </span>
                </v-list-item-content>
              </template>

              <template slot="append" v-if="isFreeDomain">
                <v-tooltip right max-width="330px" :debounce="200">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                        icon
                        v-bind="attrs"
                        v-on="on"
                        tag="span"
                        fab
                        @click.stop="$event.preventDefault()"
                        class="input-help"
                        :ripple="false"
                    >
                      <v-icon class="sr-icon-help"/>
                    </v-btn>
                  </template>
                  <span>{{ $t('domains.registrationPeriodFree') }}</span>
                </v-tooltip>
              </template>
            </v-select>

            <div v-if="getRenewPrice && !isFreeDomain && !registrationPeriod.premium" data-attr="renewal-price-info">
              <div class="select-info" :data-attr="`renewal-${registrationPeriod.year}-year`">{{ $t('renewalPrice') }}: {{registrationPeriod.year}} {{ $tc('year', registrationPeriod.year) }} {{ $t('for') }} {{currencyInfo.prefix}}{{numberTransform(getRenewPrice)}} {{currencyInfo.suffix}}</div>
            </div>
          </v-col>

          <v-col
              class="settings"
              cols="12" md="6"
          >
            <v-checkbox
                v-if="domainNameResponse && domainNameResponse.addons.idprotection"
                v-model="idProtection"
                inset
                class="custom-switch"
                data-attr="id-protection"
            >
              <template v-slot:label>
                <span v-html="$t('domains.whoisDefender.name')"></span><span style="white-space: nowrap;">&#160;<v-tooltip right max-width="500">
                <template v-slot:activator="{ on, attrs }">
                    <v-btn
                        icon
                        v-bind="attrs"
                        v-on="on"
                        tag="span"
                        fab
                        @click.stop="$event.preventDefault()"
                    >
                      <v-icon class="sr-icon-help"/>
                    </v-btn>
                </template>
                <span v-html="$t('domains.whoisDefender.description')"></span>
                </v-tooltip>
                </span>
                <div class="price" v-html="priceTransform(idProtectionPrice, currencyInfo.suffix, '', currencyInfo.prefix)"></div>
              </template>
            </v-checkbox>

            <v-checkbox
                v-model="giftHost"
                inset
                class="custom-switch"
                data-attr="gift-host"
            >
              <template v-slot:label>
                <v-icon class="sr-icon-gift"/>
                {{ $t('domains.giftHostMonth') }}<span style="white-space: nowrap;">&nbsp;<v-tooltip right max-width="500">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                      icon
                      v-bind="attrs"
                      v-on="on"
                      tag="span"
                      fab
                      @click.stop="$event.preventDefault()"
                  >
                    <v-icon class="sr-icon-help"/>
                  </v-btn>
                </template>
                <div v-html="$t('bulk.giftHostTooltip', {price: paymentPeriodPrice, currencySuffix: currencyInfo.suffix && '&nbsp;' + currencyInfo.suffix, currencyPrefix: currencyInfo.prefix})"></div>
              </v-tooltip>
              </span>
              </template>
            </v-checkbox>

            <v-checkbox
                v-if="currentSsl"
                v-model="ssl"
                inset
                class="custom-switch"
                data-attr="gift-host"
            >
              <template v-slot:label>
                {{currentSsl.name}}<span style="white-space: nowrap;">&#160;<v-tooltip right max-width="500">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                        icon
                        v-bind="attrs"
                        v-on="on"
                        tag="span"
                        fab
                        @click.stop="$event.preventDefault()"
                    >
                      <v-icon class="sr-icon-help"/>
                    </v-btn>
                  </template>
                  <h4>{{ $t(`popupSsl.details.title`) }}</h4>
                  <span v-for="(ssl, key) in getFilteredDetails(upsellDomainPid?.fields)" :key="key">
                    <span class="detail-name"> {{ $t(`popupSsl.details.${key}.title`) }}</span>
                    <b v-html="getSslContent(key, ssl) || '&#8212;'"></b>
                  </span>
                </v-tooltip>
              </span>
                <div class="price" v-html="priceTransform(currentSsl.price, currencyInfo.suffix, '', currencyInfo.prefix)"></div>
              </template>
            </v-checkbox>

          </v-col>
        </v-row>

        <div class="unit" v-if="registrationPeriod && registrationPeriod.premium === true">
          <p class="info-message warn py-0" v-html="$t('domains.premiumDomain')" data-attr="domain-premium"></p>
        </div>
      </template>


      <v-form ref="codeEpp" v-on:submit.prevent class="unit" data-attr="сode-epp" v-if="isCodeEpp">
        <v-text-field
            :label="$t('domains.codeEpp.name')"
            v-model.trim="сodeEpp"
            outlined
            clearable
            :rules="rulesCodeEpp"
        >
          <v-tooltip slot="append" right max-width="330px" :debounce="200">
            <template v-slot:activator="{ on }">
              <v-btn
                  icon
                  v-on="on"
                  tag="span"
                  fab
                  @click.stop="$event.preventDefault()"
                  class="input-help"
                  :ripple="false"
              >
                <v-icon class="sr-icon-help"/>
              </v-btn>
            </template>
            <span v-html="$t('domains.codeEpp.description')"></span>
          </v-tooltip>
        </v-text-field>
      </v-form>

      <template v-if="isCustomFields">
        <h4>{{ $t('domains.additionalOption.register') }}</h4>
        <v-form ref="customFields" class="custom-fields" @submit.prevent>
          <div v-for="(item, index) in domainNameResponse.customfields" :key="index" class="unit">
            <custom-field
                :input="{...item, index, domainIndex}"
                v-model="customFields[item.name]"
            />
          </div>
        </v-form>

        <div class="unit" v-if="showCopyButton">
          <div @click="showConfirmCopy = true" class="copy-btn">
            <v-icon class="sr-icon-copy"/>
            <span v-html="$t('bulk.copyValuesToAllDomains', { tld: initData.domain.tld })" />
          </div>
          <dialog-form
              content-class="dialog-form"
              v-bind="$attrs"
              v-on="$listeners"
              :show="showConfirmCopy"
              @close="showConfirmCopy = false"
              :activator="false"
              maxWidth="500"
              showActions
              :btnSubmit="$t('yes')"
              @confirm="copyValuesToSameTld"
          >
            <div v-html="$t('bulk.confirmCopyFields', {tld: initData.domain.tld})"></div>
          </dialog-form>
        </div>

      </template>

      <div class="unit" v-if="mirrorDomains || specialsTlds || uaMirrorDomain">
        <p
            v-if="uaMirrorDomain"
            class="ua-domain-mirror"
            data-attr="ua-domain-mirror"
            v-html="$t('domains.uaMirrorDomains', {uaDomain: uaMirrorDomain, ruDomain: domainTld})"
        ></p>
        <p
            v-if="mirrorDomains && domainOption.register === 'register'"
            class="info-message warn py-0"
            v-html="$t('domains.mirrorDomains', {zones: variablesList(mirrorDomains), zone1: mirrorDomains[0], otherZones: variablesList(mirrorDomains.slice(1)), domainsLink: $options.URLS.DOMAINS[lang], mirrorDomainsLink: $options.URLS.MIRROR_DOMAINS[lang]})"
            data-attr="domain-mirror"
        ></p>
        <p
            v-if="specialsTlds"
            class="info-message warn py-0"
            v-html="$t('domains.specialsTlds.'+specialsTlds, {faqTradeMarkLink: $options.URLS.FAQ_TRADE_MARK[lang]})"
            :data-attr="`specials-tld-${specialsTlds}`"
        ></p>
      </div>

    </template>

  </div>
</template>

<script>
  import { mapGetters } from "vuex";
  import { checkDomain } from "@/store/api/settings";
  import { SETTINGS_GETTERS } from "@/store/modules/settings";
  import { DOMAIN_OPT, DOMAIN_STATUS, DOMAIN_TYPE, FLOW, PROMO, TLDS, URLS, SSL_POPUP } from '@/constants';
  import { numberTransform } from '@/modules/numberTransform';
  import { priceTransform } from '@/modules/priceTransform';
  import CustomField from '@/shared/components/CustomFields/CustomField.vue';
  import * as Sentry from "@sentry/vue";
  import DialogForm from '@/shared/components/DialogForm/DialogForm.vue';

  const punycode = require('punycode');

  export default {
    name: "DomainName",
    DOMAIN_TYPE,
    URLS,
    FLOW,
    props: {
      domainKey: { type: String } ,
      domainIndex: { type: Number, default: 0 },
      editableDomainName: { type: Boolean, default: false },
      currencyInfo: { type: [Object, Array], default: () => ({}) },
      initialDomainOpt: { type: String },
      freeDomainInfo: { type: [Object, Array], default: () => ([]) },
      domainType: { type: String },
      initData: { type: Object, default: () => ({}) },
      initUpsellData: { type: [Object, Array], default: () => ([]) },
      domainsCard: { type: [Array], default: () => ([{}]) },
      bulk: { type: [Object], default: () => ({}) },
    },
    components: {
      CustomField,
      DialogForm,
    },
    data: () => ({
      loading: false,
      freeDomainText: '.hqsite.online',
      domainOption: {
        register: 'register',
        transfer: 'transferToIq',
      },
      domainName: undefined,
      domainStatus: undefined,
      registrationPeriod: undefined,
      registrationPeriods: undefined,
      idProtection: false,
      idProtectionObject: [],
      domainOpt: DOMAIN_OPT,
      domainNameStatus: DOMAIN_STATUS,
      skipDomainOpt: false,
      flow: FLOW.MULTI_DOMAIN,
      paymentPeriod: {},

      codeEpp: '',
      panel: undefined,
      giftHost: false,
      ssl: false,

      domainNameResponse: undefined,
      renewPrice: undefined,
      customFields: {},
      rulesDomainName: [],

      missingTldSimilar: false,
      previousName: '',
      currentSsl: undefined,
      upsellDomainPid: undefined,

      showConfirmCopy: false
    }),
    created() {
      this.prefilledDomain();
    },
    computed: {
      ...mapGetters('settings', {
        lang: SETTINGS_GETTERS.LANG,
        currency: SETTINGS_GETTERS.CURRENCY,
      }),
      freeDomainTldsList() {
        let list = '';

        this.freeDomainInfo.freeDomainTlds.forEach(item => { list += `<span class="chip-sm v-chip v-size--default">${item}</span>` });
        return list;
      },
      isCustomFields() {
        return !(this.domainOption.register === 'transfer' && this.domainOption.transfer === 'transferToIq') && this.domainNameResponse && this.domainNameResponse.customfields.length > 0;
      },
      registrationPeriodLabel() {
        return this.domainOption.register === 'transfer' ? this.$t('domains.transferPeriod') : this.$t('domains.registrationPeriod');
      },
      isFreeDomain() {
        return this.domainNameResponse && this.paymentPeriod?.freedomain && !this.domainNameResponse?.premium ? this.paymentPeriod?.freedomain && this.freeDomainInfo?.freeDomainTlds.includes(this.domainNameResponse.tld) : false;
      },
      idProtectionPrice() {
        const filtredList = this.idProtectionObject ? this.idProtectionObject.find(item => item.currency_code === this.currency) : undefined;
        const registrationYear = this.registrationPeriod ? this.registrationPeriod.year : 1;
        return filtredList ? filtredList.price * registrationYear : undefined;
      },
      getRenewPrice() {
        const renewList = this.domainNameResponse.billingcycles && this.registrationPeriod ? this.domainNameResponse.billingcycles.find(item => item.currency_code === this.currency && item.type === "renew" && item.year === this.registrationPeriod.year ) : undefined;
        return renewList ? renewList.price : undefined;
      },
      getBasePrice() {
        const base = this.domainNameResponse.billingcycles && this.domainNameResponse.billingcycles.find(item => item.currency_code === this.currency && item.type === "register" && item.year === 1 );
        return base ? base.price : 0;
      },
      rulesCodeEpp() {
        const rules = this.$attrs.rules || [];
        rules.push(v => v && !!v.trim() || this.$t('validation.requiredCodeEpp'));

        return rules;
      },
      isValidDomain() {
        return this.domainStatus === this.domainNameStatus.AVAILABLE || this.domainStatus === 'availableFreeDomain';
      },
      isTransferToUser () {
        return this.domainOption.register === 'transfer' && this.domainOption.transfer === 'transferToUser';
      },
      isFreeDomainOption() {
        return this.domainOption.register === 'freeDomain';
      },
      showFreeDomainOption() {
        const referrerUrl = document.referrer || '';
        return this.domainType === DOMAIN_TYPE.SITE_BUILDER || (this.domainType === DOMAIN_TYPE.EXTENDIFY && referrerUrl.includes('/cms-hosting/wordpress-guide')) || this.domainType === DOMAIN_TYPE.ASTRA;
      },
      domainNameText() {
        return this.isFreeDomainOption ? `${this.domainName}${this.freeDomainText}` : this.domainName;
      },
      domainTld() {
        return this.domainName ? `.${this.domainName.substr( this.domainName.indexOf('.') + 1 )}` : undefined;
      },
      mirrorDomains() {
        return TLDS.MIRROR.find( item => item.includes(this.domainTld));
      },
      uaMirrorDomain() {
        return Object.prototype.hasOwnProperty.call(TLDS.MIRROR_UA, this.domainTld) ? TLDS.MIRROR_UA[this.domainTld] : false;
      },
      specialsTlds() {
        return this.domainTld && TLDS.SPECIAL.includes(this.domainTld) ? this.domainTld.slice(1) : '';
      },
      paymentPeriodPrice() {
        return this.paymentPeriod?.price || '0.00'
      },
      isCodeEpp() {
        const flag = this.domainNameResponse && this.domainNameResponse.epp && this.domainOption.register === 'transfer';

        return flag;
      },
      notRenewedAfterTransfer () {
        return this.domainOption.register === 'transfer' && this.domainNameResponse?.status === DOMAIN_STATUS.UNAVAILABLE && this.domainNameResponse?.not_renewed_after_transfer;
      },
      showCopyButton() {
        if (!this.initData?.domain?.tld) return false;
        const currentTld = this.initData.domain.tld;
        const count = this.domainsCard.filter(
          (d) => d.domain?.tld === currentTld
        ).length;
        return count > 1;
      },
      customFieldsFromDomainsCard() {
        return this.domainsCard?.[this.domainIndex]?.customfields || {};
      },
      translationKey() {
        if (this.domainStatus === DOMAIN_STATUS.UNAVAILABLE) return 'unavailableDomain';
        if (this.domainStatus === DOMAIN_STATUS.MISSED_TLD) return 'missedTldDomain';
        return this.domainStatus;
      }
    },
    async mounted() {
      if (!this.lang) { this.lang =  await this.setLang() };
      if (!this.currency) { this.currency =  this.setCurrency() };
    },
    methods: {
      numberTransform,
      priceTransform,
      prefilledDomain() {
        this.$nextTick(() => {
          if(this.initData.domainoption && (this.initData.domainoption === 'register' || this.initData.domainoption === 'transfer')) {
            this.domainOption.register = this.initData.domainoption;
          }
          if(this.initData.domain) {
            this.domainName = Array.isArray(this.initData.domain) ? this.initData.domain[0] : this.initData.domain;
            this.$nextTick(this.checkDomainName);
          }

          if(this.showFreeDomainOption && this.domainType !== DOMAIN_TYPE.ASTRA) {
            this.domainOption.register = 'freeDomain';
          }
        });
        setTimeout(() => { this.emitBulkChanges(); }, 2000)
      },
      variablesList(list){
        return list.slice(0, -1).join(', ').concat(list.length > 1 ? ` ${this.$t('and')} ` : '', list.slice(-1));
      },
      async domainNameValidate() {
        const domainNameConverted = punycode.toASCII(this.domainName);
        this.registrationPeriods = undefined;
        this.domainNameResponse = undefined;
        this.customFields = {};
        this.codeEpp = '';

        try {
          this.loading = true;
          const domainName = this.isFreeDomainOption ? `${domainNameConverted}${this.freeDomainText}` : domainNameConverted;
          this.domainNameResponse = await checkDomain(domainName);
          this.domainStatus = this.domainNameResponse.status;
          this.idProtectionObject = this.domainNameResponse.addons_pricing ? this.domainNameResponse.addons_pricing.idprotection : undefined;

          this.getRegPeriods();
          this.computedDomainStatus();
          if(this.isValidDomain) {
            this.emitPaymentPeriod();
          }
        } catch (error) {
          Sentry.captureException(error);
          this.domainStatus = DOMAIN_STATUS.INVALID;
        } finally {
          this.loading = false;
          this.emitDomainName();
        }
      },
      checkDomainName() {
        this.rulesDomainName = [];
        this.missingTldSimilar = false;

        if(this.initialDomainOpt === DOMAIN_OPT.REQUIRED) {
          this.rulesDomainName.push(v => !!v || this.$t('validation.required') );
        }

        if(this.isFreeDomainOption) {
          this.rulesDomainName.push(v => /^[\p{L}\d\-.]*$/u.test(v || '') || this.$t('validation.invalidFreeDomainName'));
          this.rulesDomainName.push(v => /^(xn--)?([a-z0-9_])([a-z0-9_-]*)([a-z_])$/.test(v || '') || this.$t('validation.invalidFreeDomainName'));
        } else {
          this.rulesDomainName.push(v => /^[\p{L}\d\-.]*$/u.test(v ? punycode.toASCII(v) : '') || this.$t('validation.invalidDomainCharacters'));

          if (this.domainOption.register === 'register') {
            this.rulesDomainName.push(v => {

              const domain = v ? punycode.toASCII(v) : '';
              const missedTld = (!domain.includes('.') || domain.endsWith('.'));
              const correctSymbols = /^(?!-)((xn--)?[a-z0-9-_]{0,61}[a-z0-9]{1,1}.)*(xn--)?([a-z0-9-]{1,61}|[a-z0-9-]{1,30})$/.test(domain);

              const correctAlphabet = !( /[\p{Script=Cyrillic}]/u.test(v) && /[a-zA-Z]/.test(v) );

              if (missedTld && !correctAlphabet) {
                return this.$t('validation.mixedAlphabetsDomain');
              }

              if (missedTld && !correctSymbols) {
                return this.$t('validation.invalidDomainCharacters');
              }

              if (missedTld) {
                this.missingTldSimilar = true;
                if (!this.domainNameResponse && this.missingTldSimilar) {
                  this.domainStatus = "missedTld";
                }
                return '';
              }
              return true;
            });
          }

          this.rulesDomainName.push(v => /^(((?!-))(xn--)?[a-z0-9-_]{0,61}[a-z0-9]{1,1}.)*(xn--)?([a-z0-9-]{1,61}|[a-z0-9-]{1,30})\.((xn--)?[a-z0-9_-]{0,61}[a-z0-9]{1,1}|[a-z]{2,})$/.test(v ? punycode.toASCII(v) : '') || this.$t('validation.invalidDomainName'));
        }

        this.rulesDomainName.push(v => {
          if (!v) return true;
          const parts = v.split('.');
          const sld = parts.length > 1 ? parts.slice(0, 1).join('.') : parts[0];
          return !/[\p{Script=Cyrillic}]/u.test(sld) || !/[a-zA-Z]/.test(sld) || this.$t('validation.mixedAlphabetsDomain');
        });

        this.$nextTick(() => {
          if(!this.$refs.form.validate()) {
            if (this.missingTldSimilar) {
              this.domainStatus = DOMAIN_STATUS.MISSED_TLD;
            }
          } else {
            this.domainNameValidate();
          }
        });

      },
      computedDomainStatus() {
        let status;

        if(this.domainNameResponse?.status === DOMAIN_STATUS.AVAILABLE && this.domainOption.register === "transfer") {
          status = 'availableRegistration';
        } else if(this.domainNameResponse?.status === DOMAIN_STATUS.UNAVAILABLE && this.domainOption.register === "transfer" && this.registrationPeriods.length > 0) {
          status = 'available';
        }

        if(this.isTransferToUser && this.domainNameResponse?.owndomain === DOMAIN_STATUS.AVAILABLE) {
          status = 'available';
        }

        if(this.isFreeDomainOption) {
          if(this.domainNameResponse?.status === DOMAIN_STATUS.AVAILABLE) {
            status = 'availableFreeDomain';
          } else if(this.domainNameResponse?.status === DOMAIN_STATUS.UNAVAILABLE) {
            status = 'unavailableFreeDomain';
          } else if(this.domainNameResponse?.status === DOMAIN_STATUS.IDN_NOT_ALLOWED) {
            status = 'idnNotAllowedFreeDomain';
          }
        }

        if(!this.isFreeDomainOption && this.registrationPeriods?.length <= 0 && !this.isTransferToUser) {
          status = 'tld_not_supported';
        }

        if(this.domainNameResponse?.status === DOMAIN_STATUS.INVALID) {
          status = 'notAvailable';
        } else if(this.domainNameResponse?.status === DOMAIN_STATUS.TOOSHORT || this.domainNameResponse?.status === DOMAIN_STATUS.TOOLONG) {
          ({ status } = this.domainNameResponse)
        } else if(this.domainNameResponse?.status === DOMAIN_STATUS.IS_TLD) {
          status = 'unavailableFreeDomain';
        } else if(this.domainNameResponse?.status === DOMAIN_STATUS.IS_LATIN_AND_NON_LATIN) {
          status = 'latin_and_non_latin';
        }

        if (!this.domainNameResponse && this.missingTldSimilar) {
          status = "missedTld";
        }

        const matches = this.domainsCard.filter(
          (item) =>
            item.domain &&
            item.domain.isValidDomain === true &&
            item.domain.name === this.domainName
        )
        if (matches.length > 1) {
          status = "domainAlreadyAdded";
        }

        this.domainStatus = status || this.domainNameResponse.status;
      },
      getRegPeriods() {
        if(this.isFreeDomainOption) {
          this.registrationPeriods = [];
          this.registrationPeriod = undefined;
        } else {
          const billingcycles = this.domainNameResponse.billingcycles.filter(item => item.currency_code === this.currency);

          if(this.isFreeDomain && this.domainOption.register === "register") {
            this.registrationPeriods = [{ price: '0.00', year: 1 }];
          } else if(this.domainOption.register === "register") {
            this.registrationPeriods = billingcycles.filter(item => item.type === "register");
          } else if(this.domainOption.register === "transfer") {
            this.registrationPeriods = billingcycles.filter(item => item.type === "transfer");
          }

          this.registrationPeriod = this.registrationPeriod ? this.registrationPeriods.find(item => item.year === this.registrationPeriod.year) : this.registrationPeriods[0];

          if(this.isValidDomain === false || this.isTransferToUser) {
            this.registrationPeriod = undefined;
          }
        }
        this.emitDomainName();
      },
      emitDomainName() {
        let domainName = {};
        if(this.isValidDomain === true) {
          domainName = {
            key: 'domain-name',
            id: this.domainKey,
            title: this.domainOption.register,
            name: this.domainName,
            price: this.registrationPeriod ? this.registrationPeriod.price : undefined,
            year: this.registrationPeriod ? this.registrationPeriod.year : undefined,
            renewal: this.registrationPeriod ? this.getRenewPrice : 0,
            idProtection: this.idProtectionObject ? this.idProtectionObject : undefined,
            isValidDomain: true,
            freeDomain: this.isFreeDomain,
            tld: this.domainTld
          };

          if(this.isFreeDomainOption || this.isTransferToUser || this.notRenewedAfterTransfer) {
            domainName = { key: 'domain-name', isValidDomain: true, name: this.domainName };
          }
          if(this.registrationPeriod && this.registrationPeriod.premium === true) {
            domainName.renewal = '0.00'
          }
          if(this.isFreeDomain) {
            domainName.basePrice = this.getBasePrice
          }
        } else {
          domainName = { key: 'domain-name', isValidDomain: false, name: this.domainName };
        }

        this.$emit('domainName', domainName);
      },
      emitPaymentPeriod() {
        const currentCycle = this.domainNameResponse && this.domainNameResponse.promo_hosting ? this.domainNameResponse.promo_hosting.billingcycles.find(item => item.currency_code === this.currency && item.available === true && item.period === "monthly") : {};
        if(this.giftHost) {
          const period = {
            key: 'payment-period',
            title: this.domainNameResponse?.promo_hosting?.group?.translated_names?.[this.lang.toLowerCase()] || this.domainNameResponse?.promo_hosting?.group?.name,
            name: this.domainNameResponse?.promo_hosting?.name,
            month: currentCycle.month,
            period: currentCycle.period,
            price: currentCycle.price,
            renewal: currentCycle.price,
            currency: currentCycle.currency_code,
            freedomain: currentCycle.freedomain,
            promo: PROMO.DOMAIN_GIFT,
            basePrice: currentCycle.price
          };
          this.$emit('paymentPeriod', period);
          this.$emit('dataPeriod', { options: this.domainNameResponse?.promo_hosting?.config_opts, cycle: [currentCycle] });
          this.paymentPeriod = period;

        } else {
          this.$emit('paymentPeriod', { key: 'payment-period', isHidden: true, promo: undefined });
          this.$emit('dataPeriod', { options: [], cycle: [currentCycle] });
        }
      },
      validateCustomFields() {
        let isValid = true;

        if (this.isValidDomain) {
          const needsEpp = this.domainNameResponse?.epp && this.domainOption.register === 'transfer' && this.domainOption.transfer === 'transferToIq';
          const eppValid = needsEpp ? this.$refs.codeEpp?.validate?.() : true;
          const customFieldsValid = this.isCustomFields ? this.$refs.customFields?.validate?.() : true;

          isValid = eppValid && customFieldsValid;

          if (!isValid) {
            this.$nextTick(() => {
              const el = this.$el.querySelector(".v-messages.error--text:first-of-type") || this.$el.querySelector(".error--text");
              if (el) this.$vuetify.goTo(el, { offset: 80 });
            });
          }

        } else {
          isValid = false;
          const el = this.$refs[`domainBlock-${this.domainIndex}`];
          const target = el?.$el || (el instanceof HTMLElement ? el : null);
          if (target) this.$vuetify.goTo(target, { offset: 80 });
        }

        return isValid;
      },
      commitDomainOption() {
        const domainOption = (this.domainOption.register === 'transfer' && this.domainOption.transfer === 'transferToUser')
          ? 'ownDomain' : this.domainOption.register;
      },
      getSimilarDomainPrice(price, year) {
        let result;
        if(price) {
          result = year ? price.find(item => item.currency_code === this.currency).year : price.find(item => item.currency_code === this.currency).price
        }
          return result
      },
      convertToUnicode(domain) {
        return punycode.toUnicode(domain);
      },
      getCurrentSsl(val) {
        const initUpsellData = val || this.initUpsellData;
        if(initUpsellData.length > 0) {
          let ssl = {};
          this.upsellDomainPid = initUpsellData.find(item => item.pid === "217");

          ssl = this.upsellDomainPid.product.billingcycles.filter( item => item.currency_code === this.currency);

          this.currentSsl = {
            ...ssl.find(item => item.month === 12),
            name: this.upsellDomainPid?.name
          };
        }
      },
      getFilteredDetails(fields) {
        return Object.keys(fields)
          .filter(key => SSL_POPUP.DETAILS.includes(key) && (key !== 'sealInSearch' || fields[key]))
          .reduce((obj, key) => {
            obj[key] = fields[key];
            return obj;
          }, {});
      },
      getSslContent(key, item) {
        switch (key) {
          case 'issuance':
          case 'validation':
            return this.$t(`popupSsl.details.${key}.${item}.dropdown`);
          case 'subdomainsProtection':
          case 'companyNameInSSLDetails':
          case 'mobileSupport':
          case 'sealInSearch':
            return item ? '+' : '&#8212;';
          default:
            return this.$t(`popupSsl.details.${key}.${item}`) || '&#8212;';
        }
      },
      copyValuesToSameTld() {
        const currentTld = this.initData.domain.tld;
        const currentFields = this.customFields;

        this.domainsCard.forEach((item, idx) => {
          if (item.domain &&
            item.domain.tld === currentTld &&
            idx !== this.domainIndex
          ) {
            this.$set(this.domainsCard, idx, { ...this.domainsCard[idx], customfields: { ...currentFields }});
          }
        });
      },
      emitBulkChanges() {
        this.$emit('updateBulkData', {
          customfields: this.customFields || undefined,
          ssl: { value: this.ssl, ...this.currentSsl },
          hostingGift: { value: this.giftHost, price: this.paymentPeriodPrice, renewal: this.paymentPeriod?.renewal },
          ...(this.domainNameResponse?.addons?.idprotection && { idprotection: { value: this.idProtection, price: this.idProtectionPrice } }),
          registrationPeriod: this.registrationPeriod,
          type: "register"
        });
      }
    },
    watch: {
      "initUpsellData": {
        handler(val) {
          this.getCurrentSsl(val);
        },
        immediate: true,
        deep: true,
      },
      "isValidDomain": {
        handler(val) {
          if(this.domainNameResponse) { this.getRegPeriods() };

          if(!val) { this.idProtection = false };
          this.emitDomainName();
          this.commitDomainOption();
          this.$emit('validDomain', val);
        },
        deep: true,
      },
      domainName(val) {
        this.$nextTick(() => {
          if(val) {
            this.domainName = val.replace(/\s/g, '').toLowerCase();
          }
        });
        this.domainNameResponse = undefined;
        this.domainStatus = undefined;
        this.missingTldSimilar = false;
      },
      "currencyInfo": {
        handler() {
          if(this.domainNameResponse) { this.getRegPeriods() };
          if(this.isValidDomain) { this.emitPaymentPeriod() };
          this.$emit('customFields', this.customFields);
        },
        deep: true
      },
      registrationPeriod(val) {
        this.getCurrentSsl();
        if(val) { this.emitDomainName() };
        this.emitBulkChanges();
      },
      "domainOption": {
        handler() {
          if(this.domainNameResponse) {
            this.getRegPeriods();
            this.computedDomainStatus();
            this.commitDomainOption();
          }

          if(this.flow === FLOW.DOMAIN) {
            if(this.domainOption.register === 'transfer') {
              this.domainOption.transfer = 'transferToIq';
            }
          }

          if (this.domainOption.register !== 'register' && this.missingTldSimilar) {
            this.domainStatus = undefined;
            this.missingTldSimilar = false;
          }

          setTimeout(() => {
            if(this.domainName !== undefined && !this.domainNameResponse && !this.loading) {
              this.checkDomainName();
            }
          }, 1000)

        },
        deep: true,
      },
      isFreeDomain() {
        if(this.domainNameResponse) { this.getRegPeriods() };
      },
      isFreeDomainOption() {
        this.domainName = '';
        this.domainNameResponse = undefined;
      },
      "loading": {
        handler(val) {
          this.$emit('loading', val);
        },
        immediate: true,
        deep: true
      },
      "customFields": {
        handler() {
          this.$emit('customFields', this.customFields);
        },
        immediate: true,
        deep: true
      },
      "customFieldsFromDomainsCard": {
        handler(newVal) {
          if (JSON.stringify(newVal) !== JSON.stringify(this.customFields)) {
            this.customFields = { ...newVal };
            this.$emit('customFields', this.customFields);
            this.emitBulkChanges();
          }
        },
        immediate: true,
        deep: true
      },

      ssl() {
        this.emitBulkChanges();
      },
      giftHost() {
        this.emitPaymentPeriod();
        this.emitBulkChanges();
      },
      idProtection() {
        this.emitBulkChanges();
      },
      'bulk.ssl': function onBulkSslChange(newVal) {
        this.ssl = newVal;
      },
      'bulk.hostingGift': function onBulkHostingGiftChange(newVal) {
        this.giftHost = newVal;
      },
      'bulk.idprotection': function onBulkWhoisChange(newVal) {
        this.idProtection = newVal;
      },
      'bulk.registrationPeriod': function onBulkRegistrationPeriodChange(newVal) {
        if (!newVal || !Array.isArray(this.registrationPeriods)) return;

        const found = this.registrationPeriods.find(p => p.year === Number(newVal));
        if (found) {
          this.registrationPeriod = found;
        }
      },
    }
  }
</script>

<style lang="scss">
  .domain-card {

    .select-info {
      max-width: 100%;
      padding-top: 10px;
    }

    .domain-settings {
      align-items: flex-start;
      justify-content: space-between;

      .period {
        max-width: 45%;

        @media (max-width: $phablet) {
          max-width: 100%;
          width: 100%;
        }
      }

      .settings {
        position: relative;


        @media (min-width: $phablet) {
          max-width: 55%;
          flex: 0 0 55%;
          padding-left: 28px;

          &:before {
            content: '';
            display: inline-flex;
            height: 100%;
            width: 1px;
            background-color: $gray-soft;
            position: absolute;
            top: 0;
            left: 8px;
          }
        }

        .theme--light.v-input .theme--light.v-label,
        .price-transform sub {
          font-size: 15px !important;
        }

        .v-tooltip + .v-btn {
          margin-right: 0;
          margin-top: -3px;

          .v-icon {
            margin: 0;
          }
        }

        .custom-switch .v-input--selection-controls__input {
          margin-top: 0 5px 0 -3px;
        }
      }
    }

    .custom-fields {

      &:has(> .unit:nth-child(3)) {
        display: flex;
        flex-wrap: wrap;
        gap: 20px 16px;
        align-items: baseline;

        > .unit {
          width: calc(50% - 8px);
          padding-bottom: 0 !important;

          @media (max-width: $tablet) {
            width: 100%;
          }
        }
      }
    }

    .copy-btn {
      font-size: 15px;
      color: $gray;
      transition: $transition;
      display: inline-flex;
      align-items: center;

      i {
        color: $gray;
        font-size: 15px;
        margin-right: 3px;
      }

      &:hover,
      &:hover i{
        cursor: pointer;
        color: $black;
      }
    }
  }
</style>
